"use client";
import { useEffect, useRef, useState, FormEvent } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { UploadCloud, CheckCircle2, ArrowRight } from "lucide-react";
import { api, useApp } from "./provider";
import { Gate, PageTitle } from "./shell";
import type { Word } from "@/lib/types";
export function Upload() {
  return (
    <Gate>
      <UploadForm />
    </Gate>
  );
}
function UploadForm() {
  const { t, locale, languages, categories, user, error, message } = useApp(),
    params = useSearchParams(),
    [mode, setMode] = useState("existing"),
    [words, setWords] = useState<Word[]>([]),
    [language, setLanguage] = useState(user?.preferred_language || "asl"),
    [word, setWord] = useState(params.get("word") || ""),
    [file, setFile] = useState<File | null>(null),
    [preview, setPreview] = useState(""),
    [busy, setBusy] = useState(false),
    [progress, setProgress] = useState(0),
    [done, setDone] = useState(false),
    [wordPage, setWordPage] = useState(0),
    [wordsMore, setWordsMore] = useState(false),
    [wordsLoading, setWordsLoading] = useState(false),
    [wordsFailed, setWordsFailed] = useState(false),
    [wordsRetry, setWordsRetry] = useState(0),
    [selectedWord, setSelectedWord] = useState<Word | null>(null);
  const submitting = useRef(false);
  useEffect(() => {
    let alive = true;
    const selected = params.get("word");
    if (selected)
      api<{ word: Word }>(`word/${encodeURIComponent(selected)}`)
        .then((d) => { if (alive) { setLanguage(d.word.language_id); setWord(d.word.id); setSelectedWord(d.word); } })
        .catch(e => { if (alive) error(e); });
    return () => { alive = false; };
  }, [params, error]);
  useEffect(() => { setWordPage(0); }, [language]);
  useEffect(() => {
    let alive = true;
    setWordsLoading(true); setWordsFailed(false);
    api<Word[]>(`words?${new URLSearchParams({ language, sort: "az", page: String(wordPage) })}`)
      .then(value => {
        if (!alive) return;
        setWords(previous => {
          const next = wordPage ? [...previous, ...value] : value;
          return next.filter((item, index) => next.findIndex(other => other.id === item.id) === index);
        });
        setWordsMore((wordPage + 1) * 20 < (value[0]?.total_count || 0));
      })
      .catch(e => { if (alive) { setWordsFailed(true); error(e); } })
      .finally(() => { if (alive) setWordsLoading(false); });
    return () => { alive = false; };
  }, [language, wordPage, wordsRetry, error]);
  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    if (!file) {
      error(new Error("file_type"));
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      error(new Error("file_size"));
      return;
    }
    setBusy(true);
    submitting.current = true;
    setProgress(0);
    const values = Object.fromEntries(new FormData(event.currentTarget));
    const metadata: any = {
      region: values.region,
      community: values.community,
      explanation: values.explanation,
      notes: values.notes,
      consent: values.consent === "on",
    };
    if (mode === "existing") metadata.word_id = word;
    else
      metadata.new_word = {
        en: values.en,
        zh: values.zh,
        meaning_en: values.meaning_en,
        meaning_zh: values.meaning_zh,
        language_id: language,
        category_id: values.category_id,
        tags: String(values.tags || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
    const form = new FormData();
    form.set("video", file);
    form.set("metadata", JSON.stringify(metadata));
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", "/api/upload");
        xhr.timeout = 120000;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable)
            setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          let result;
          try {
            result = JSON.parse(xhr.responseText);
          } catch {
            reject(new Error("unavailable"));
            return;
          }
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(result.error || "unavailable"));
        };
        xhr.onerror = () => reject(new Error("unavailable"));
        xhr.ontimeout = () => reject(new Error("unavailable"));
        xhr.onabort = () => reject(new Error("unavailable"));
        xhr.send(form);
      });
      setDone(true);
    } catch (e) {
      error(e);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }
  if (done)
    return (
      <div className="container narrow success-panel">
        <CheckCircle2 size={55} />
        <h2>{t("Your sign is in good hands", "您的手语已成功提交")}</h2>
        <p>
          {t(
            "Your video is queued for processing and community review. It will become visible after a moderator approves it. Follow its progress in My contributions.",
            "您的视频正在等待处理和社区审核，审核员批准后才会公开。您可以在“我的贡献”中跟踪进度。",
          )}
        </p>
        <Link href="/contributions" className="button primary">
          {t("View my contributions", "查看我的贡献")}
          <ArrowRight size={17} />
        </Link>
        <button
          className="button"
          onClick={() => {
            setDone(false);
            setFile(null);
          }}
        >
          {t("Share another sign", "再分享一个手语")}
        </button>
      </div>
    );
  return (
    <div className="container">
      <PageTitle
        eyebrow={t("MAKE YOUR COMMUNITY VISIBLE", "让您的社区被看见")}
        title={t(
          "Share a sign, grow the dictionary.",
          "分享手语，让词典成长。",
        )}
        description={t(
          "A regional variation. A missing word. Your knowledge can help someone understand.",
          "一种地域变体，一个缺失的词语。您的知识可以帮助他人理解。",
        )}
      />
      {!user?.verified && (
        <div className="notice warning">
          {t(
            "Please verify your email before submitting a sign.",
            "提交手语前请先验证您的邮箱。",
          )}{" "}
          <button
            className="text-button"
            onClick={async () => {
              try {
                await api("auth/resend", {});
                message(t("Verification email sent.", "验证邮件已发送。"));
              } catch (e) {
                error(e);
              }
            }}
          >
            {t("Resend verification email", "重新发送验证邮件")}
          </button>
        </div>
      )}
      <div className="upload-layout">
        <form className="form-panel" onSubmit={submit}>
          <div
            className="tabs"
            role="tablist"
            aria-label={t("Submission type", "投稿类型")}
          >
            <button
              role="tab"
              type="button"
              aria-selected={mode === "existing"}
              className={mode === "existing" ? "selected" : ""}
              onClick={() => setMode("existing")}
            >
              {t("Add a variation", "添加变体")}
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={mode === "new"}
              className={mode === "new" ? "selected" : ""}
              onClick={() => setMode("new")}
            >
              {t("Suggest a new word", "提出新词")}
            </button>
          </div>
          <div className="form-grid">
            <label>
              {t("Sign language", "手语语言")}
              <select
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value);
                  setWord("");
                }}
              >
                {languages.map((l) => (
                  <option key={l.id} value={l.id}>
                    {locale === "en" ? l.en : l.zh}
                  </option>
                ))}
              </select>
            </label>
            {mode === "existing" ? (
              <label>
                {t("Dictionary word", "词典词语")}
                <select
                  required
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                >
                  <option value="">{t("Choose a word", "选择词语")}</option>
                  {selectedWord?.language_id === language && !words.some(item => item.id === selectedWord.id) && <option value={selectedWord.id}>{selectedWord.en} / {selectedWord.zh}</option>}
                  {words.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.en} / {w.zh}
                    </option>
                  ))}
                </select>
                {wordsLoading && <small role="status">{t("Loading words…", "正在加载词语…")}</small>}
                {wordsFailed ? <button type="button" className="text-button" onClick={() => setWordsRetry(value => value + 1)}>{t("Retry word list", "重试词语列表")}</button> : wordsMore && <button type="button" className="text-button" disabled={wordsLoading} onClick={() => setWordPage(value => value + 1)}>{t("Load more words", "加载更多词语")}</button>}
              </label>
            ) : (
              <label>
                {t("Category", "分类")}
                <select name="category_id" required>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {locale === "en" ? c.en : c.zh}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {mode === "new" && (
              <>
                <label>
                  {t("English word", "英文词语")}
                  <input name="en" required maxLength={100} />
                </label>
                <label>
                  {t("Chinese word", "中文词语")}
                  <input name="zh" required maxLength={100} />
                </label>
                <label>
                  {t("Meaning in English", "英文释义")}
                  <textarea name="meaning_en" required maxLength={1000} />
                </label>
                <label>
                  {t("Meaning in Chinese", "中文释义")}
                  <textarea name="meaning_zh" required maxLength={1000} />
                </label>
                <label className="wide">
                  {t("Tags, separated by commas", "标签，以逗号分隔")}
                  <input name="tags" maxLength={400} />
                </label>
              </>
            )}
          </div>
          <label className="dropzone">
            <UploadCloud size={30} />
            <strong>
              {file
                ? file.name
                : t("Choose your sign video", "选择您的手语视频")}
            </strong>
            <small>
              {t(
                "MP4 or MOV · Up to 50 MB · Up to 2 minutes",
                "MP4或MOV · 最大50 MB · 最长2分钟",
              )}
            </small>
            <input
              type="file"
              accept="video/mp4,video/quicktime,.mov"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
            />
          </label>
          {preview && (
            <video
              className="preview-video"
              src={preview}
              controls
              playsInline
            />
          )}
          <div className="form-grid">
            <label>
              {t("Region", "地区")}
              <input
                name="region"
                required
                maxLength={100}
                placeholder={t("City, state, or country", "城市、省份或国家")}
                defaultValue={user?.region}
              />
            </label>
            <label>
              {t("Community (optional)", "社区（选填）")}
              <input name="community" maxLength={150} />
            </label>
            <label className="wide">
              {t("Explain your variation", "说明您的变体")}
              <textarea
                name="explanation"
                maxLength={2000}
                placeholder={t(
                  "Where is this sign used? What makes it different?",
                  "这个手语在哪里使用？有什么特点？",
                )}
              />
            </label>
            <label className="wide">
              {t("Additional notes (optional)", "补充说明（选填）")}
              <textarea name="notes" maxLength={2000} />
            </label>
          </div>
          <label className="checkbox">
            <input type="checkbox" name="consent" required />
            {t(
              "I have permission from everyone shown in this video and agree to share it publicly after moderation.",
              "我已获得视频中所有人的许可，并同意视频经审核后公开分享。",
            )}
          </label>
          {busy && (
            <>
              <progress value={progress} max={100} />
              <p role="status">
                {progress < 100
                  ? t(`Uploading ${progress}%…`, `上传中 ${progress}%…`)
                  : t("Saving your contribution…", "正在保存您的投稿…")}
              </p>
            </>
          )}
          <button className="button primary" disabled={busy || !user?.verified}>
            {busy
              ? t("Submitting…", "提交中…")
              : t("Share this variation", "分享此变体")}
            <ArrowRight size={17} />
          </button>
        </form>
        <aside className="upload-tips form-panel">
          <h3>
            {t("A clear sign makes a difference.", "清晰的手语更容易理解。")}
          </h3>
          <ol>
            <li>
              {t(
                "Keep your face, hands, and upper body in the frame.",
                "确保脸部、双手和上半身都在画面中。",
              )}
            </li>
            <li>
              {t(
                "Use good lighting and a plain background.",
                "使用充足的光线和简洁的背景。",
              )}
            </li>
            <li>
              {t(
                "Sign naturally, then repeat once at a slower pace.",
                "自然地打手语，再放慢速度重复一次。",
              )}
            </li>
            <li>
              {t(
                "Describe where and how your community uses the sign.",
                "说明您的社区在哪里以及如何使用这个手语。",
              )}
            </li>
          </ol>
          <div className="filter-divider" />
          <h3>{t("What happens next?", "接下来会怎样？")}</h3>
          <div className="steps">
            <span>{t("Upload", "上传")}</span>→
            <span>{t("Process", "处理")}</span>→
            <span>{t("Review", "审核")}</span>→
            <span>{t("Publish", "公开")}</span>
          </div>
          <p>
            {t(
              "Every submission is reviewed. An approved video earns 10 reputation points; a new approved word earns 20 more.",
              "每份投稿都会经过审核。视频获批可获得10点声望，新词获批可额外获得20点。",
            )}
          </p>
        </aside>
      </div>
    </div>
  );
}
